import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import { useKeyShortcuts } from 'hooks/use-key-shortcuts';
import {
  getGridModeToolbarShortcutBindings,
  getGridModeToolbarTooltipText
} from './grid-mode-toolbar-helpers';
import { GridModeToolbarItem } from './grid-mode-toolbar-item';

type GridModeToolbarProps<T extends string> = Readonly<{
  mode: T;
  items: GridModeToolbarItem<T>[];
  idPrefix: string;
  onChange: (mode: T) => void;
  isItemDisabled?: (mode: T) => boolean;
  disabledTooltip?: string;
}>;

export default function GridModeToolbar<T extends string>({
  mode,
  items,
  idPrefix,
  onChange,
  isItemDisabled,
  disabledTooltip
}: GridModeToolbarProps<T>) {
  useKeyShortcuts({
    bindings: getGridModeToolbarShortcutBindings(
      items,
      onChange,
      isItemDisabled
    )
  });

  return (
    <div className='absolute right-2 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-1'>
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = mode === item.id;
        const isDisabled = isItemDisabled?.(item.id) ?? false;
        return (
          <button
            key={item.id}
            id={`${idPrefix}-${item.id}`}
            type='button'
            disabled={isDisabled}
            className={clsx(
              'relative inline-flex items-center justify-center rounded-md border p-2.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              isActive
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            )}
            onClick={() => onChange(item.id)}
          >
            <Icon className='h-7 w-7' />
            <Tooltip
              text={getGridModeToolbarTooltipText(
                item,
                isDisabled,
                disabledTooltip
              )}
              options={{ placement: 'left' }}
            />
          </button>
        );
      })}
    </div>
  );
}
